import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Download, Send, Trash2, Bell, Edit, CheckCircle, RotateCcw } from 'lucide-react';
import { formatCurrency } from '../utils/formatCurrency';
import { useAuth } from '../context/AuthContext';
import SendInvoiceDialog from '../components/SendInvoiceDialog';
import EditInvoiceDialog from '../components/EditInvoiceDialog';
import MarkAsPaidDialog from '../components/MarkAsPaidDialog';
import AdminStatusChangeDialog from '../components/AdminStatusChangeDialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Invoices = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [customerFilter, setCustomerFilter] = useState('all');
  const [monthFilter, setMonthFilter] = useState('all');
  const [yearFilter, setYearFilter] = useState('all');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [invoiceToSend, setInvoiceToSend] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [invoiceToEdit, setInvoiceToEdit] = useState(null);
  const [paidDialogOpen, setPaidDialogOpen] = useState(false);
  const [invoiceToMarkPaid, setInvoiceToMarkPaid] = useState(null);
  const [adminStatusDialogOpen, setAdminStatusDialogOpen] = useState(false);
  const [invoiceToChangeStatus, setInvoiceToChangeStatus] = useState(null);

  const isAdmin = user?.role === 'admin';

  const statusConfig = {
    draft: { label: t('invoices.statusDraft'), color: 'bg-slate-100 text-slate-600 border-slate-200' },
    sent: { label: t('invoices.statusSent'), color: 'bg-blue-50 text-blue-600 border-blue-200' },
    paid: { label: t('invoices.statusPaid'), color: 'bg-emerald-50 text-emerald-600 border-emerald-200' },
    overdue: { label: t('invoices.statusOverdue'), color: 'bg-amber-50 text-amber-600 border-amber-200' },
  };

  useEffect(() => {
    fetchInvoices();
    fetchCustomers();
  }, []);

  // Use useMemo to avoid infinite loops
  const filteredInvoices = useMemo(() => {
    let filtered = [...invoices];

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(inv => inv.status === statusFilter);
    }

    // Customer filter
    if (customerFilter !== 'all') {
      filtered = filtered.filter(inv => inv.customer_id === customerFilter);
    }

    // Month filter
    if (monthFilter !== 'all') {
      filtered = filtered.filter(inv => {
        const invDate = new Date(inv.invoice_date);
        return (invDate.getMonth() + 1) === parseInt(monthFilter);
      });
    }

    // Year filter
    if (yearFilter !== 'all') {
      filtered = filtered.filter(inv => {
        const invDate = new Date(inv.invoice_date);
        return invDate.getFullYear() === parseInt(yearFilter);
      });
    }

    return filtered;
  }, [statusFilter, customerFilter, monthFilter, yearFilter, invoices]);

  // Get available years from invoices
  const availableYears = useMemo(() => {
    const years = new Set();
    invoices.forEach(inv => {
      const year = new Date(inv.invoice_date).getFullYear();
      years.add(year);
    });
    return Array.from(years).sort((a, b) => b - a); // Sort descending (newest first)
  }, [invoices]);

  const fetchInvoices = async () => {
    try {
      const response = await axios.get(`${API}/invoices`);
      setInvoices(response.data);
    } catch (error) {
      toast.error(t('invoices.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`);
      setCustomers(response.data);
    } catch (error) {
      console.error(t('customers.loadError'));
    }
  };

  const handleDownload = async (invoiceId, invoiceNumber) => {
    try {
      const response = await axios.get(`${API}/invoices/${invoiceId}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Rechnung_${invoiceNumber.replace('/', '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(t('invoices.downloadSuccess'));
    } catch (error) {
      toast.error(t('invoices.downloadError'));
    }
  };

  const handleOpenSendDialog = async (invoice) => {
    // Fetch customer details to get CC emails
    try {
      const response = await axios.get(`${API}/customers/${invoice.customer_id}`);
      const customer = response.data;
      
      // Combine invoice and customer data
      setInvoiceToSend({
        ...invoice,
        customer_cc_emails: customer.cc_emails || []
      });
      setSendDialogOpen(true);
    } catch (error) {
      // If customer fetch fails, just open dialog without CC emails
      setInvoiceToSend(invoice);
      setSendDialogOpen(true);
    }
  };

  const handleSend = async (ccEmails) => {
    try {
      const response = await axios.post(`${API}/invoices/${invoiceToSend.id}/send`, {
        cc_emails: ccEmails
      });
      
      const ccInfo = response.data.cc_count > 0 
        ? t('invoices.sendSuccessMultiple', { count: response.data.cc_count + 1 })
        : t('invoices.sendSuccess');
      toast.success(ccInfo);
      
      fetchInvoices();
    } catch (error) {
      toast.error(t('invoices.sendError'));
      throw error;
    }
  };

  const handleSendReminder = async (invoiceId) => {
    try {
      const response = await axios.post(`${API}/invoices/${invoiceId}/send-reminder`);
      toast.success(t('invoices.reminderSuccess', { days: response.data.days_overdue }));
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('invoices.reminderError'));
    }
  };

  const handleSendAllReminders = async () => {
    try {
      const response = await axios.post(`${API}/invoices/send-all-reminders`);
      if (response.data.reminders_sent > 0) {
        toast.success(t('invoices.allRemindersSuccess', { count: response.data.reminders_sent }));
      } else {
        toast.info(t('invoices.noOverdueInvoices'));
      }
      if (response.data.errors && response.data.errors.length > 0) {
        toast.warning(t('invoices.reminderErrors', { count: response.data.errors.length }));
      }
      fetchInvoices();
    } catch (error) {
      toast.error(t('invoices.reminderError'));
    }
  };

  const handleStatusChange = async (invoiceId, newStatus, invoice) => {
    // If changing to paid, open the payment dialog
    if (newStatus === 'paid') {
      setInvoiceToMarkPaid(invoice);
      setPaidDialogOpen(true);
      return;
    }
    
    try {
      await axios.put(`${API}/invoices/${invoiceId}`, { status: newStatus });
      toast.success(t('invoices.statusUpdateSuccess'));
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('invoices.statusUpdateError'));
    }
  };

  const handleMarkAsPaid = async (paymentData) => {
    try {
      await axios.put(`${API}/invoices/${invoiceToMarkPaid.id}`, paymentData);
      toast.success(t('invoices.markedAsPaid'));
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('invoices.statusUpdateError'));
      throw error;
    }
  };

  const handleEditInvoice = async (updateData) => {
    try {
      await axios.put(`${API}/invoices/${invoiceToEdit.id}`, updateData);
      toast.success(t('invoices.updateSuccess'));
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('invoices.updateError'));
      throw error;
    }
  };

  const handleAdminStatusChange = async (statusData) => {
    try {
      await axios.put(`${API}/invoices/${invoiceToChangeStatus.id}`, statusData);
      toast.success(t('invoices.statusUpdateSuccess'));
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('invoices.statusUpdateError'));
      throw error;
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/invoices/${invoiceToDelete.id}`);
      toast.success(t('invoices.deleteSuccess'));
      fetchInvoices();
    } catch (error) {
      toast.error(t('invoices.deleteError'));
    } finally {
      setDeleteDialogOpen(false);
      setInvoiceToDelete(null);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-slate-500">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 md:p-8 lg:p-12 space-y-6 sm:space-y-8">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div>
          <h1 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-slate-900" data-testid="invoices-title">
            {t('invoices.title')}
          </h1>
          <p className="text-slate-500 mt-1 text-sm sm:text-base">{t('invoices.subtitle')}</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
          <Button 
            variant="outline"
            onClick={handleSendAllReminders}
            data-testid="send-all-reminders-button"
            className="w-full sm:w-auto rounded-sm border-amber-600 text-amber-600 hover:bg-amber-50 text-sm"
          >
            <Bell className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">{t('invoices.sendAllReminders')}</span>
            <span className="sm:hidden">{t('invoices.sendReminder')}</span>
          </Button>
          <Link to="/invoices/create" className="w-full sm:w-auto">
            <Button className="w-full sm:w-auto rounded-sm bg-primary hover:bg-[#D62839] shadow-none text-sm">
              <Plus className="w-4 h-4 mr-2" />
              {t('invoices.newInvoice')}
            </Button>
          </Link>
        </div>
      </div>

      {/* Filter */}
      <Card className="border-slate-200 shadow-none rounded-sm">
        <CardContent className="py-4">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
            <span className="text-sm font-medium text-slate-700">{t('common.filter')}:</span>
            
            {/* Status Filter */}
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 flex-wrap">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-40 rounded-sm" data-testid="status-filter">
                  <SelectValue placeholder={t('invoices.status')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('invoices.allStatus')}</SelectItem>
                  <SelectItem value="draft">{t('invoices.statusDraft')}</SelectItem>
                  <SelectItem value="sent">{t('invoices.statusSent')}</SelectItem>
                  <SelectItem value="paid">{t('invoices.statusPaid')}</SelectItem>
                  <SelectItem value="overdue">{t('invoices.statusOverdue')}</SelectItem>
                </SelectContent>
              </Select>

              {/* Customer Filter */}
              <Select value={customerFilter} onValueChange={setCustomerFilter}>
                <SelectTrigger className="w-full sm:w-48 rounded-sm" data-testid="customer-filter">
                  <SelectValue placeholder={t('invoices.filterByCustomer')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('invoices.allCustomers')}</SelectItem>
                  {customers.map((customer) => (
                    <SelectItem key={customer.id} value={customer.id}>
                      {customer.company_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Year Filter */}
              <Select value={yearFilter} onValueChange={setYearFilter}>
                <SelectTrigger className="w-full sm:w-32 rounded-sm" data-testid="year-filter">
                  <SelectValue placeholder={t('invoices.filterByYear')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('invoices.allYears')}</SelectItem>
                  {availableYears.map((year) => (
                    <SelectItem key={year} value={year.toString()}>
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Month Filter */}
              <Select value={monthFilter} onValueChange={setMonthFilter}>
                <SelectTrigger className="w-full sm:w-36 rounded-sm" data-testid="month-filter">
                  <SelectValue placeholder={t('invoices.filterByMonth')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('invoices.allMonths')}</SelectItem>
                  <SelectItem value="1">{t('months.january')}</SelectItem>
                  <SelectItem value="2">{t('months.february')}</SelectItem>
                  <SelectItem value="3">{t('months.march')}</SelectItem>
                  <SelectItem value="4">{t('months.april')}</SelectItem>
                  <SelectItem value="5">{t('months.may')}</SelectItem>
                  <SelectItem value="6">{t('months.june')}</SelectItem>
                  <SelectItem value="7">{t('months.july')}</SelectItem>
                  <SelectItem value="8">{t('months.august')}</SelectItem>
                  <SelectItem value="9">{t('months.september')}</SelectItem>
                  <SelectItem value="10">{t('months.october')}</SelectItem>
                  <SelectItem value="11">{t('months.november')}</SelectItem>
                  <SelectItem value="12">{t('months.december')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <span className="text-sm text-slate-500 lg:ml-auto">
              {filteredInvoices.length} {filteredInvoices.length === 1 ? t('invoices.invoiceCount', { count: 1 }) : t('invoices.invoiceCount_plural', { count: filteredInvoices.length })}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Invoices List */}
      {filteredInvoices.length === 0 ? (
        <Card className="border-slate-200 shadow-none rounded-sm">
          <CardContent className="py-12">
            <div className="text-center text-slate-400">
              <p>{t('invoices.noInvoices')}</p>
              <p className="text-sm mt-1">{t('invoices.createFirst')}</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredInvoices.map((invoice) => (
            <Card key={invoice.id} className="border-slate-200 shadow-none rounded-sm hover:shadow-sm transition-shadow" data-testid="invoice-card">
              <CardContent className="py-3 sm:py-4">
                {/* Mobile Layout */}
                <div className="block lg:hidden space-y-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="font-mono text-sm font-bold text-slate-900">#{invoice.invoice_number}</p>
                      <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{invoice.customer_data.company_name}</p>
                    </div>
                    <Badge className={`rounded-full ${statusConfig[invoice.status]?.color} border text-xs`}>
                      {statusConfig[invoice.status]?.label}
                    </Badge>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div>
                      <p className="text-slate-500">{t('invoices.invoiceDate')}</p>
                      <p className="font-mono text-slate-900">{formatDate(invoice.invoice_date)}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">{t('invoices.dueDate')}</p>
                      <p className="font-mono text-slate-900">{formatDate(invoice.due_date)}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                    <p className="font-mono text-sm font-bold text-slate-900">{formatCurrency(invoice.total_amount)}</p>
                    <div className="flex gap-1">
                      {invoice.status !== 'paid' && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setInvoiceToEdit(invoice);
                            setEditDialogOpen(true);
                          }}
                          className="h-8 w-8 rounded-sm hover:bg-slate-100"
                          title={t('invoices.editInvoice')}
                        >
                          <Edit className="w-4 h-4 text-slate-600" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDownload(invoice.id, invoice.invoice_number)}
                        className="h-8 w-8 rounded-sm hover:bg-slate-100"
                      >
                        <Download className="w-4 h-4 text-slate-600" />
                      </Button>
                      {invoice.status !== 'paid' && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenSendDialog(invoice)}
                          className="h-8 w-8 rounded-sm hover:bg-slate-100"
                          title={t('invoices.resendInvoice')}
                        >
                          <Send className="w-4 h-4 text-slate-600" />
                        </Button>
                      )}
                      {invoice.status !== 'paid' && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setInvoiceToMarkPaid(invoice);
                            setPaidDialogOpen(true);
                          }}
                          className="h-8 w-8 rounded-sm hover:bg-emerald-100"
                          title={t('invoices.markAsPaid')}
                        >
                          <CheckCircle className="w-4 h-4 text-emerald-600" />
                        </Button>
                      )}
                      {invoice.status === 'overdue' && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleSendReminder(invoice.id)}
                          className="h-8 w-8 rounded-sm hover:bg-amber-100"
                        >
                          <Bell className="w-4 h-4 text-amber-600" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          setInvoiceToDelete(invoice);
                          setDeleteDialogOpen(true);
                        }}
                        className="h-8 w-8 rounded-sm hover:bg-slate-100"
                      >
                        <Trash2 className="w-4 h-4 text-slate-600" />
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Desktop Layout */}
                <div className="hidden lg:flex items-center justify-between gap-4">
                  <div className="flex-1 grid grid-cols-5 gap-4 items-center">
                    <div>
                      <p className="font-mono text-sm font-bold text-slate-900">#{invoice.invoice_number}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{invoice.customer_data.company_name}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">{t('invoices.invoiceDate')}</p>
                      <p className="font-mono text-sm text-slate-900">{formatDate(invoice.invoice_date)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">{t('invoices.dueDate')}</p>
                      <p className="font-mono text-sm text-slate-900">{formatDate(invoice.due_date)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">{t('invoices.amount')}</p>
                      <p className="font-mono text-sm font-bold text-slate-900">{formatCurrency(invoice.total_amount)}</p>
                    </div>
                    <div>
                      {invoice.status === 'paid' ? (
                        <Badge className={`rounded-full ${statusConfig[invoice.status]?.color} border`}>
                          {statusConfig[invoice.status]?.label}
                        </Badge>
                      ) : (
                        <Select value={invoice.status} onValueChange={(value) => handleStatusChange(invoice.id, value, invoice)}>
                          <SelectTrigger className="rounded-sm h-8">
                            <Badge className={`rounded-full ${statusConfig[invoice.status]?.color} border`}>
                              {statusConfig[invoice.status]?.label}
                            </Badge>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="draft">{t('invoices.statusDraft')}</SelectItem>
                            <SelectItem value="sent">{t('invoices.statusSent')}</SelectItem>
                            <SelectItem value="paid">{t('invoices.statusPaid')}</SelectItem>
                            <SelectItem value="overdue">{t('invoices.statusOverdue')}</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    {invoice.status !== 'paid' && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          setInvoiceToEdit(invoice);
                          setEditDialogOpen(true);
                        }}
                        data-testid={`edit-invoice-${invoice.id}`}
                        className="h-9 w-9 rounded-sm hover:bg-slate-100"
                        title={t('invoices.editInvoice')}
                      >
                        <Edit className="w-4 h-4 text-slate-600" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDownload(invoice.id, invoice.invoice_number)}
                      data-testid={`download-invoice-${invoice.id}`}
                      className="h-9 w-9 rounded-sm hover:bg-slate-100"
                      title={t('invoices.downloadPdf')}
                    >
                      <Download className="w-4 h-4 text-slate-600" />
                    </Button>
                    {invoice.status !== 'paid' && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleOpenSendDialog(invoice)}
                        data-testid={`send-invoice-${invoice.id}`}
                        className="h-9 w-9 rounded-sm hover:bg-slate-100"
                        title={t('invoices.resendInvoice')}
                      >
                        <Send className="w-4 h-4 text-slate-600" />
                      </Button>
                    )}
                    {invoice.status !== 'paid' && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          setInvoiceToMarkPaid(invoice);
                          setPaidDialogOpen(true);
                        }}
                        data-testid={`mark-paid-invoice-${invoice.id}`}
                        className="h-9 w-9 rounded-sm hover:bg-emerald-100"
                        title={t('invoices.markAsPaid')}
                      >
                        <CheckCircle className="w-4 h-4 text-emerald-600" />
                      </Button>
                    )}
                    {invoice.status === 'overdue' && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleSendReminder(invoice.id)}
                        data-testid={`reminder-invoice-${invoice.id}`}
                        className="h-9 w-9 rounded-sm hover:bg-amber-100"
                        title={t('invoices.sendReminder')}
                      >
                        <Bell className="w-4 h-4 text-amber-600" />
                      </Button>
                    )}
                    {invoice.status === 'paid' && isAdmin && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          setInvoiceToChangeStatus(invoice);
                          setAdminStatusDialogOpen(true);
                        }}
                        data-testid={`revert-status-${invoice.id}`}
                        className="h-9 w-9 rounded-sm hover:bg-amber-100"
                        title={t('invoices.adminChangeStatus')}
                      >
                        <RotateCcw className="w-4 h-4 text-amber-600" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        setInvoiceToDelete(invoice);
                        setDeleteDialogOpen(true);
                      }}
                      data-testid={`delete-invoice-${invoice.id}`}
                      className="h-9 w-9 rounded-sm hover:bg-slate-100"
                      title={t('invoices.deleteInvoice')}
                    >
                      <Trash2 className="w-4 h-4 text-slate-600" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('invoices.deleteInvoice')}?</AlertDialogTitle>
            <AlertDialogDescription>
              {t('invoices.deleteConfirm', { number: invoiceToDelete?.invoice_number })} {t('invoices.deleteWarning')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="rounded-sm bg-primary hover:bg-[#D62839]">
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Send Invoice Dialog */}
      {invoiceToSend && (
        <SendInvoiceDialog
          open={sendDialogOpen}
          onOpenChange={setSendDialogOpen}
          invoiceNumber={invoiceToSend.invoice_number}
          customerEmail={invoiceToSend.customer_data?.email || ''}
          defaultCcEmails={invoiceToSend.customer_cc_emails || []}
          onSend={handleSend}
        />
      )}

      {/* Edit Invoice Dialog */}
      {invoiceToEdit && (
        <EditInvoiceDialog
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
          invoice={invoiceToEdit}
          onSave={handleEditInvoice}
        />
      )}

      {/* Mark as Paid Dialog */}
      {invoiceToMarkPaid && (
        <MarkAsPaidDialog
          open={paidDialogOpen}
          onOpenChange={setPaidDialogOpen}
          invoice={invoiceToMarkPaid}
          onConfirm={handleMarkAsPaid}
        />
      )}

      {/* Admin Status Change Dialog */}
      {invoiceToChangeStatus && (
        <AdminStatusChangeDialog
          open={adminStatusDialogOpen}
          onOpenChange={setAdminStatusDialogOpen}
          invoice={invoiceToChangeStatus}
          onConfirm={handleAdminStatusChange}
        />
      )}
    </div>
  );
};

export default Invoices;